import type { FeToolResult } from "@/services/agent/feToolRegistry";
import { agentSurfaceRegistry, registerAgentSurface } from "@/services/agent/agentSurface/registry";

export type AskAniHandoffOptions = {
  initialDraft?: string;
  scopeText?: string | null;
};

/** Live review surface state published for in-app agent context. */
export type ReviewSurfaceLiveContext = {
  taskId?: string;
  artifactId?: string;
  surfaceTab?: string;
  openCommentCount?: number;
  askAniOpen?: boolean;
  reviewSelection?: string;
};

export const TASK_REVIEW_WORKSPACE_SURFACE_ID = "task-review.workspace";
export const ARTIFACT_EDITOR_SURFACE_ID = "artifact.editor";

const OPEN_REVIEW_LETTER_ACTION = "open_review_letter";
const OPEN_ASK_ANI_ACTION = "open_ask_ani";

type EditorBridgeHandlers = {
  openAskAni: (opts?: AskAniHandoffOptions) => void;
  getLiveContext: () => ReviewSurfaceLiveContext;
};

type WorkspaceBridgeHandlers = {
  openLetterForAskAni: (opts?: AskAniHandoffOptions) => boolean;
  getLiveContext: () => ReviewSurfaceLiveContext;
};

export function registerReviewSurfaceAgentBridge(handlers: EditorBridgeHandlers): () => void {
  return registerAgentSurface({
    surfaceId: ARTIFACT_EDITOR_SURFACE_ID,
    appId: "artifact",
    getLiveContext: () => handlers.getLiveContext(),
    actions: {
      [OPEN_ASK_ANI_ACTION]: (params) => executeAskAniOnEditor(handlers, params),
    },
  });
}

/** Register while TaskReviewWorkspace is mounted (brief or letter view). */
export function registerReviewWorkspaceAgentBridge(handlers: WorkspaceBridgeHandlers): () => void {
  const unregister = registerAgentSurface({
    surfaceId: TASK_REVIEW_WORKSPACE_SURFACE_ID,
    appId: "task-review",
    getLiveContext: () => handlers.getLiveContext(),
    actions: {
      [OPEN_REVIEW_LETTER_ACTION]: () => executeOpenReviewLetterOnWorkspace(handlers),
      [OPEN_ASK_ANI_ACTION]: (params) => executeAskAniOnWorkspace(handlers, params),
    },
  });

  const pendingLetter = agentSurfaceRegistry.consumePending(
    TASK_REVIEW_WORKSPACE_SURFACE_ID,
    OPEN_REVIEW_LETTER_ACTION,
  );
  if (pendingLetter) {
    handlers.openLetterForAskAni();
  }
  const pendingAsk = agentSurfaceRegistry.consumePending(
    TASK_REVIEW_WORKSPACE_SURFACE_ID,
    OPEN_ASK_ANI_ACTION,
  );
  if (pendingAsk) {
    handlers.openLetterForAskAni(parseAskAniHandoff(pendingAsk));
  }

  return unregister;
}

export function consumePendingAskAniHandoff(): AskAniHandoffOptions | null {
  const pending = agentSurfaceRegistry.consumePending(
    TASK_REVIEW_WORKSPACE_SURFACE_ID,
    OPEN_ASK_ANI_ACTION,
  );
  return pending ? parseAskAniHandoff(pending) : null;
}

export function consumePendingOpenReviewLetter(): boolean {
  return (
    agentSurfaceRegistry.consumePending(
      TASK_REVIEW_WORKSPACE_SURFACE_ID,
      OPEN_REVIEW_LETTER_ACTION,
    ) !== null
  );
}

export function hasPendingReviewLetterOpen(): boolean {
  return agentSurfaceRegistry.hasPending(TASK_REVIEW_WORKSPACE_SURFACE_ID);
}

export function getReviewSurfaceLiveContext(): ReviewSurfaceLiveContext | null {
  const workspace = agentSurfaceRegistry.getLiveContext(TASK_REVIEW_WORKSPACE_SURFACE_ID) as
    | ReviewSurfaceLiveContext
    | null;
  const editor = agentSurfaceRegistry.getLiveContext(ARTIFACT_EDITOR_SURFACE_ID) as
    | ReviewSurfaceLiveContext
    | null;
  if (!workspace && !editor) {
    return null;
  }
  return { ...(workspace ?? {}), ...(editor ?? {}) };
}

function parseAskAniHandoff(params: Record<string, unknown>): AskAniHandoffOptions {
  const initialDraftRaw = params.draft;
  const initialDraft =
    typeof initialDraftRaw === "string" && initialDraftRaw.trim()
      ? initialDraftRaw.trim()
      : undefined;

  let scopeText: string | null | undefined;
  if (params.scope_text === null || params.scopeText === null) {
    scopeText = null;
  } else {
    const scopeRaw = params.scope_text ?? params.scopeText;
    scopeText =
      typeof scopeRaw === "string" && scopeRaw.trim() ? scopeRaw.trim() : undefined;
  }

  return { initialDraft, scopeText };
}

function executeOpenReviewLetterOnWorkspace(
  handlers: WorkspaceBridgeHandlers,
): FeToolResult {
  if (handlers.openLetterForAskAni()) {
    return { success: true, message: "Opened the suitability letter in review." };
  }
  agentSurfaceRegistry.queuePending(
    TASK_REVIEW_WORKSPACE_SURFACE_ID,
    OPEN_REVIEW_LETTER_ACTION,
    {},
  );
  return {
    success: false,
    message: "Review documents are still loading — try again in a moment.",
  };
}

function executeAskAniOnEditor(
  handlers: EditorBridgeHandlers,
  params: Record<string, unknown>,
): FeToolResult {
  const handoffOpts = parseAskAniHandoff(params);
  handlers.openAskAni(handoffOpts);
  return {
    success: true,
    message: handoffOpts.initialDraft
      ? "Opened Ask Ani with your draft ready to send."
      : "Opened Ask Ani in the letter drawer.",
  };
}

function executeAskAniOnWorkspace(
  handlers: WorkspaceBridgeHandlers,
  params: Record<string, unknown>,
): FeToolResult {
  const handoffOpts = parseAskAniHandoff(params);
  const opened = handlers.openLetterForAskAni(handoffOpts);
  if (!opened) {
    agentSurfaceRegistry.queuePending(
      TASK_REVIEW_WORKSPACE_SURFACE_ID,
      OPEN_ASK_ANI_ACTION,
      params,
    );
    agentSurfaceRegistry.queuePending(
      TASK_REVIEW_WORKSPACE_SURFACE_ID,
      OPEN_REVIEW_LETTER_ACTION,
      {},
    );
    return {
      success: false,
      message: "Review documents are still loading — try again in a moment.",
    };
  }
  return {
    success: true,
    message: handoffOpts.initialDraft
      ? "Opening the letter and Ask Ani with your draft ready to send."
      : "Opening the letter and Ask Ani drawer.",
  };
}

function isRefLive(ref: string, live: ReviewSurfaceLiveContext | null): boolean {
  const [kind, id = ""] = ref.split("/")[0].split(":");
  const value = kind === "artifact" ? live?.artifactId : kind === "review" ? live?.taskId : undefined;
  return Boolean(value) && value?.toLowerCase() === id.trim().toLowerCase();
}

/**
 * Open Ask Ani on the letter surface. With `ref` (artifact:<id> or review:<task_id>) the surface is
 * opened first via `showRef`; when it has not mounted yet the action is queued and runs on register.
 */
export async function executeAskAniHandoff(
  params: Record<string, unknown>,
  showRef: (ref: string) => Promise<FeToolResult>,
): Promise<FeToolResult> {
  const ref = String(params.ref ?? "").trim();
  if (ref && !/^(artifact|review):/.test(ref)) {
    return { success: false, message: "Ask Ani opens on a letter — use ref artifact:<id> or review:<task_id>" };
  }

  if (ref && !isRefLive(ref, getReviewSurfaceLiveContext())) {
    const shown = await showRef(ref.split("/")[0]);
    if (!shown.success) {
      return shown;
    }
    const surfaceId = ref.startsWith("artifact:") ? ARTIFACT_EDITOR_SURFACE_ID : TASK_REVIEW_WORKSPACE_SURFACE_ID;
    if (!agentSurfaceRegistry.getLiveContext(surfaceId)) {
      agentSurfaceRegistry.queuePending(surfaceId, OPEN_ASK_ANI_ACTION, params);
      return { success: true, message: "Opening Ask Ani as soon as the letter loads." };
    }
  }

  if (agentSurfaceRegistry.getLiveContext(ARTIFACT_EDITOR_SURFACE_ID)) {
    return agentSurfaceRegistry.execute(ARTIFACT_EDITOR_SURFACE_ID, OPEN_ASK_ANI_ACTION, params);
  }
  if (agentSurfaceRegistry.getLiveContext(TASK_REVIEW_WORKSPACE_SURFACE_ID)) {
    return agentSurfaceRegistry.execute(TASK_REVIEW_WORKSPACE_SURFACE_ID, OPEN_ASK_ANI_ACTION, params);
  }
  return { success: false, message: "No letter is open — pass ref artifact:<id> or review:<task_id>" };
}
